/**
 * ContactFAB — a floating "Talk to us" pill visible on every public
 * page. Bottom-LEFT (Sancta chat owns bottom-right).
 *
 * Behaviour:
 *  · Auto-hides on any /workspace/*, /admin/*, /login, /signup,
 *    /onboarding path so it doesn't clash with logged-in surfaces.
 *  · Tap → opens a compact popover with 4 channel shortcuts
 *    (WhatsApp, Email, Call, Contact form).
 *  · Persists in localStorage so users who dismiss it don't see it
 *    again for 24 h.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Mail, Phone, X, Sparkles, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { COMPANY } from "@/lib/company";

const HIDDEN_PREFIXES = [
  "/workspace", "/admin", "/superadmin", "/login", "/signup",
  "/onboarding", "/reset-password", "/forgot-password", "/verify",
];

const DISMISS_KEY = "socibaba.contactFabDismissedUntil";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Poll the public site config once per mount to decide whether the FAB
 * should show. Default is OFF (per Super Admin request) — the widget only
 * appears when contact_fab_enabled = true in Admin Setup → Site Branding.
 * We fetch the *public* endpoint directly (no auth wrapper needed) so
 * anonymous visitors get the same answer as logged-in users.
 */
function useContactFabEnabled(): boolean {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const q = useQuery<{ contact_fab_enabled?: boolean }>({
    queryKey: ["public-site-config-contact-fab"],
    queryFn: async () => (await axios.get(`${backend}/api/site-config/public`)).data,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  return !!q.data?.contact_fab_enabled;
}

export const ContactFAB: React.FC = () => {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [consentInWay, setConsentInWay] = useState(false);
  const enabled = useContactFabEnabled();

  const hidden = useMemo(
    () => HIDDEN_PREFIXES.some((p) => loc.pathname.startsWith(p)),
    [loc.pathname],
  );

  useEffect(() => {
    try {
      const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (until && Date.now() < until) setDismissed(true);
    } catch { /* localStorage might be disabled */ }
  }, []);

  // Defer to the ConsentBanner while it's on-screen — they share the
  // bottom-left corner and stacking them is worse UX than waiting.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const check = () => setConsentInWay(!!document.querySelector('[data-testid="consent-banner"]'));
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  if (!enabled || hidden || dismissed || consentInWay) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_TTL_MS));
    } catch { /* ignore */ }
    setDismissed(true);
    setOpen(false);
  };

  return (
    <div
      className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-40 flex flex-col items-start gap-2 pointer-events-none"
      data-testid="contact-fab-root"
    >
      <AnimatePresence>
        {open && (
          <motion.div
            key="menu"
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.18 }}
            className="pointer-events-auto bg-white rounded-2xl border border-slate-200 shadow-2xl p-3 w-[calc(100vw-2rem)] sm:w-72 max-w-[19rem]"
            data-testid="contact-fab-panel"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-fuchsia-700 flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> Talk to a human
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="contact-fab-close"
                aria-label="Close"
                className="h-6 w-6 rounded-full hover:bg-slate-100 flex items-center justify-center"
              >
                <X className="h-3.5 w-3.5 text-slate-500" />
              </button>
            </div>
            <div className="space-y-1.5">
              <FabRow
                testid="contact-fab-whatsapp"
                href={COMPANY.whatsappUrl}
                external
                icon={<MessageCircle className="h-4 w-4 text-white" />}
                iconBg="bg-emerald-500"
                title="WhatsApp"
                sub={COMPANY.customerCarePhone}
              />
              <FabRow
                testid="contact-fab-email"
                href={`mailto:${COMPANY.supportEmail}`}
                icon={<Mail className="h-4 w-4 text-white" />}
                iconBg="bg-fuchsia-600"
                title="Email us"
                sub={COMPANY.supportEmail}
              />
              <FabRow
                testid="contact-fab-phone"
                href={`tel:${COMPANY.customerCarePhoneRaw}`}
                icon={<Phone className="h-4 w-4 text-white" />}
                iconBg="bg-orange-500"
                title="Call us"
                sub={COMPANY.supportHours}
              />
              <FabRow
                testid="contact-fab-form"
                href="/contact"
                icon={<ArrowRight className="h-4 w-4 text-white" />}
                iconBg="bg-slate-900"
                title="Open contact form"
                sub="Categorised, tracked, one-day reply"
                internal
              />
            </div>
            <div className="pt-2 mt-2 border-t border-slate-100 flex items-center justify-between">
              <button
                type="button"
                onClick={dismiss}
                data-testid="contact-fab-dismiss"
                className="text-[11px] text-slate-500 hover:text-slate-700 transition-colors"
              >
                Hide for a day
              </button>
              <span className="text-[10px] text-slate-400">Fast · human · private</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="contact-fab-btn"
        aria-label="Contact SociBaba"
        aria-expanded={open}
        whileTap={{ scale: 0.95 }}
        className="pointer-events-auto flex items-center gap-2 rounded-full bg-slate-900 hover:bg-black text-white pl-2.5 pr-4 py-2 shadow-lg shadow-black/20 border border-black/20"
      >
        <span className="h-7 w-7 rounded-full bg-gradient-to-br from-fuchsia-500 to-orange-500 flex items-center justify-center">
          <MessageCircle className="h-3.5 w-3.5 text-white" />
        </span>
        <span className="text-sm font-semibold hidden sm:inline">Contact us</span>
        <span className="text-sm font-semibold sm:hidden">Contact</span>
      </motion.button>
    </div>
  );
};

const FabRow: React.FC<{
  testid?: string; href: string; internal?: boolean; external?: boolean;
  icon: React.ReactNode; iconBg: string; title: string; sub: string;
}> = ({ testid, href, internal, external, icon, iconBg, title, sub }) => {
  const inner = (
    <div className="flex items-center gap-2.5 rounded-lg border border-slate-100 hover:border-slate-200 hover:bg-slate-50/60 px-2.5 py-2 transition">
      <span className={`h-8 w-8 rounded-md ${iconBg} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="text-[11px] text-slate-500 truncate">{sub}</div>
      </div>
    </div>
  );
  if (internal) return <Link to={href} data-testid={testid} className="block">{inner}</Link>;
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      data-testid={testid}
      className="block"
    >
      {inner}
    </a>
  );
};

export default ContactFAB;
